// @flow
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import AffordableWaitlistService from '../../../../../services/affordableWaitlistService';
import type { CountsByStatus } from '../types';
import type { Props as ModalProps } from './index';

export const ENABLE_WAITLIST_FIELD = 'setup.affordableWaitlist';

const NOT_STARTED = 'NOT_STARTED';
const EMPTY_COUNTS: CountsByStatus = {
  Waitlist: 0,
  'In Process': 0,
  Canceled: 0,
  Denied: 0,
};

const affordableWaitlistService = new AffordableWaitlistService();

type SwitchEvent = { target: { name: string, value: boolean } };
type Candidates = { count: number, countsByStatus: CountsByStatus };
type ConfirmSave = (proceed: boolean) => void;

export function isV2Enabled(setup: ?Object): boolean {
  return (
    setup?.affordableWaitlist === true && setup?.waitlistV2?.enabled === true
  );
}

function settleConfirm(
  confirmSaveRef: { current: ?ConfirmSave },
  proceed: boolean,
) {
  const resolve = confirmSaveRef.current;
  confirmSaveRef.current = null;
  resolve?.(proceed);
}

export function useWaitlistV2Enablement({
  organizationId,
  propertyId,
  isFlagOn,
  editMode,
  originalSetup,
  onChange,
}: {
  organizationId: string,
  propertyId: string,
  isFlagOn: boolean,
  editMode: boolean,
  originalSetup: ?Object,
  onChange: (evt: SwitchEvent) => void,
}): {
  handleEnableWaitlistChange: (evt: SwitchEvent) => Promise<void>,
  confirmBeforeSave: (currentSetup: Object) => Promise<boolean>,
  handleSaveSucceeded: (savedSetup: Object) => Promise<void>,
  modalProps: ModalProps,
} {
  const queryClient = useQueryClient();
  const isEligible = isFlagOn && !isV2Enabled(originalSetup);
  const wasSwitchOn = originalSetup?.affordableWaitlist === true;

  // Kept apart from the open flag so the copy does not change while the modal animates closed.
  const [candidates, setCandidates] = useState<Candidates>({
    count: 0,
    countsByStatus: EMPTY_COUNTS,
  });
  const [isEnableOpen, setIsEnableOpen] = useState(false);
  const [isStartedOpen, setIsStartedOpen] = useState(false);
  const [isNotStartedOpen, setIsNotStartedOpen] = useState(false);

  const isMountedRef = useRef(true);
  const isEditingRef = useRef(editMode);
  // Changes whenever edit mode does, so a response from an earlier edit is dropped.
  const editSessionRef = useRef(0);
  const isEnablingRef = useRef(false);
  const isAcknowledgedRef = useRef(false);
  // Survives the end of edit mode: the save that ends it still needs this edit's status.
  const statusRef = useRef<?string>(null);
  // Resolves the Save that modal 1 is holding (trigger B).
  const confirmSaveRef = useRef<?ConfirmSave>(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      settleConfirm(confirmSaveRef, false);
    };
  }, []);

  useEffect(() => {
    isEditingRef.current = editMode;
    editSessionRef.current += 1;
    if (editMode) {
      statusRef.current = null;
      return;
    }
    isEnablingRef.current = false;
    isAcknowledgedRef.current = false;
    settleConfirm(confirmSaveRef, false);
    setIsEnableOpen(false);
  }, [editMode]);

  const readStatus = useCallback(async (): Promise<?string> => {
    const response = await queryClient.fetchQuery(
      ['waitlistMigrationStatus', organizationId, propertyId],
      () =>
        affordableWaitlistService.getWaitlistMigrationStatus(
          organizationId,
          propertyId,
        ),
      { staleTime: 0 },
    );
    return response?.status;
  }, [queryClient, organizationId, propertyId]);

  const readEnablement = useCallback(
    (): Promise<[?string, Candidates]> =>
      Promise.all([
        readStatus(),
        queryClient.fetchQuery(
          ['applicationsNeedingInfo', organizationId, propertyId],
          () =>
            affordableWaitlistService.getApplicationsNeedingInfo(
              organizationId,
              propertyId,
            ),
          { staleTime: 0 },
        ),
      ]),
    [readStatus, queryClient, organizationId, propertyId],
  );

  const openEnableModal = useCallback((response: Candidates) => {
    setCandidates({
      count: response?.count ?? 0,
      countsByStatus: response?.countsByStatus ?? EMPTY_COUNTS,
    });
    setIsEnableOpen(true);
  }, []);

  const handleEnableWaitlistChange = useCallback(
    async (evt: SwitchEvent) => {
      onChange(evt);
      if (!isEligible) return;
      isEnablingRef.current = Boolean(evt.target.value);
      if (!isEnablingRef.current) {
        setIsEnableOpen(false);
        // A Save held on this read was made with the switch ON; it no longer applies.
        settleConfirm(confirmSaveRef, false);
        return;
      }

      const session = editSessionRef.current;
      try {
        const [status, response] = await readEnablement();
        const isCurrent =
          isMountedRef.current &&
          isEditingRef.current &&
          session === editSessionRef.current;
        if (!isCurrent) return;
        statusRef.current = status;
        // A Save that arrived during this read holds on it and opens modal 1 itself.
        if (confirmSaveRef.current) return;
        if (isEnablingRef.current && status === NOT_STARTED) {
          openEnableModal(response);
        }
      } catch {
        // The check is informational; a failure must not block enabling.
      }
    },
    [onChange, isEligible, readEnablement, openEnableModal],
  );

  const confirmBeforeSave = useCallback(
    (currentSetup: Object): Promise<boolean> => {
      if (
        !isEligible ||
        currentSetup?.affordableWaitlist !== true ||
        isAcknowledgedRef.current
      ) {
        return Promise.resolve(true);
      }
      // A second Save while the first still waits on modal 1 must not save twice.
      if (confirmSaveRef.current) return Promise.resolve(false);

      return new Promise((resolve) => {
        confirmSaveRef.current = resolve;
        // fetchQuery dedupes on its key, so a trigger A read in flight is shared, not repeated.
        readEnablement().then(
          ([status, response]) => {
            // Settled already: edit mode ended or the page unmounted.
            if (confirmSaveRef.current !== resolve) return;
            statusRef.current = status;
            if (status === NOT_STARTED) {
              openEnableModal(response);
            } else {
              settleConfirm(confirmSaveRef, true);
            }
          },
          () => {
            // Informational, as in trigger A: a failed read lets the save go ahead.
            if (confirmSaveRef.current === resolve) {
              settleConfirm(confirmSaveRef, true);
            }
          },
        );
      });
    },
    [isEligible, readEnablement, openEnableModal],
  );

  const handleSaveSucceeded = useCallback(
    async (savedSetup: Object) => {
      if (!isEligible || savedSetup?.affordableWaitlist !== true) return;
      let status = statusRef.current;
      statusRef.current = null;
      if (status == null) {
        try {
          status = await readStatus();
        } catch {
          if (isMountedRef.current) setIsNotStartedOpen(true);
          return;
        }
      }
      if (status !== NOT_STARTED) return;

      try {
        await affordableWaitlistService.startWaitlistMigration(
          organizationId,
          propertyId,
        );
        if (isMountedRef.current) setIsStartedOpen(true);
      } catch {
        if (isMountedRef.current) setIsNotStartedOpen(true);
      }
    },
    [isEligible, readStatus, organizationId, propertyId],
  );

  const handleContinue = useCallback(() => {
    isAcknowledgedRef.current = true;
    setIsEnableOpen(false);
    settleConfirm(confirmSaveRef, true);
  }, []);

  const handleCancel = useCallback(() => {
    const isSaveHeld = confirmSaveRef.current != null;
    setIsEnableOpen(false);
    settleConfirm(confirmSaveRef, false);
    // At Save, turning a switch that was ON at load OFF would disable the V1 waitlist it runs.
    if (isSaveHeld && wasSwitchOn) return;
    isEnablingRef.current = false;
    onChange({ target: { name: ENABLE_WAITLIST_FIELD, value: false } });
  }, [onChange, wasSwitchOn]);

  const closeStarted = useCallback(() => setIsStartedOpen(false), []);
  const closeNotStarted = useCallback(() => setIsNotStartedOpen(false), []);

  return {
    handleEnableWaitlistChange,
    confirmBeforeSave,
    handleSaveSucceeded,
    modalProps: {
      enable: {
        open: isEnableOpen,
        count: candidates.count,
        countsByStatus: candidates.countsByStatus,
        onContinue: handleContinue,
        onCancel: handleCancel,
      },
      started: { open: isStartedOpen, onClose: closeStarted },
      notStarted: { open: isNotStartedOpen, onClose: closeNotStarted },
    },
  };
}
